import { useEffect, useRef, useState } from "react";
import { ImageIcon, Trash2Icon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

/**
 * El recuadro de arrastre de la imagen satelital, SIN saber de la API.
 *
 * Es presentacional a propósito, y la razón es concreta: la misma zona sirve
 * en dos momentos que no se parecen. En la EDICIÓN el informe ya existe y cada
 * archivo soltado se sube al toque; en el ALTA el informe todavía no tiene id
 * —lo asigna el `POST /informes`— así que no hay a dónde subir nada y el
 * archivo se retiene hasta después de crearlo. Un solo componente que hiciera
 * las dos cosas tendría que llevar adentro un `if` sobre si su recurso existe.
 *
 * Acá solo entra y sale: recibe qué mostrar, avisa qué eligió la persona.
 *
 * EL ARRASTRE NO ES LA ÚNICA PUERTA. El recuadro entero es un botón que abre
 * el selector de archivos, y se llega a él con el teclado. Arrastrar es un
 * atajo para quien tiene la captura en una carpeta abierta al lado; hacerlo
 * obligatorio dejaría afuera a quien navega con teclado.
 */

/** Espejo de `GuardarImagenDelInformeRequest::MAXIMO_KB`. */
export const MAXIMO_BYTES = 10 * 1024 * 1024;

/** Espejo de la regla `mimes:jpeg,png,webp` del servidor. */
const TIPOS_ACEPTADOS = ["image/jpeg", "image/png", "image/webp"];

/** Para el `accept` del input: extensiones, que es lo que entiende el diálogo. */
const EXTENSIONES_ACEPTADAS = ".jpg,.jpeg,.png,.webp";

/**
 * Lo que el navegador puede rechazar sin gastar la subida, o `null` si el
 * archivo pasa.
 *
 * NO es la autoridad y no pretende serlo: la validación real es la del backend
 * (`GuardarImagenDelInformeRequest`), que mira los BYTES con finfo y no el tipo
 * que declara el sistema operativo. Esto existe para no mandar 40 MB por la red
 * antes de descubrir que no entran, y su 422 se pinta igual cuando llega.
 *
 * Vive acá, compartido, para que el alta y la edición rechacen lo mismo: dos
 * copias de estos límites se desincronizan y una de las dos pantallas termina
 * aceptando lo que la otra no.
 */
export function motivoDeRechazo(archivo: File): string | null {
  if (!TIPOS_ACEPTADOS.includes(archivo.type)) {
    return "La imagen debe ser JPG, PNG o WEBP.";
  }
  if (archivo.size > MAXIMO_BYTES) {
    return "La imagen no puede pesar más de 10 MB.";
  }
  return null;
}

/**
 * La imagen que trae un `paste`, o `null` si ese pegado no es para el recuadro.
 *
 * Devuelve la primera IMAGEN aunque su formato no se acepte: un GIF pegado
 * tiene que terminar en el mismo "debe ser JPG, PNG o WEBP" que un GIF
 * soltado, no en silencio. Lo que NO es imagen se ignora sin avisar, porque
 * ese pegado casi seguro era para otro campo.
 *
 * Word y las planillas ponen TEXTO e IMAGEN a la vez en el portapapeles. Si el
 * foco está en un campo de texto y hay texto, el pegado es del campo. Una
 * imagen SOLA sí se toma desde cualquier lado: un campo de texto la
 * descartaría, así que no le quita nada a nadie.
 */
function imagenDelPortapapeles(e: ClipboardEvent): File | null {
  const datos = e.clipboardData;
  if (!datos) return null;

  const imagen = Array.from(datos.files ?? []).find((f) => f.type.startsWith("image/"));
  if (!imagen) return null;

  const traeTexto = Array.from(datos.types ?? []).includes("text/plain");
  if (traeTexto && esCampoEditable(e.target)) return null;

  return imagen;
}

function esCampoEditable(destino: EventTarget | null): boolean {
  if (!(destino instanceof HTMLElement)) return false;
  return (
    destino instanceof HTMLInputElement ||
    destino instanceof HTMLTextAreaElement ||
    destino.isContentEditable
  );
}

export function ZonaDeImagen({
  src,
  peso,
  ocupado = false,
  error,
  onArchivo,
  onQuitar,
}: {
  /** URL de la imagen a mostrar, o `null` si todavía no hay ninguna. */
  src: string | null;
  /** Tamaño en bytes, para la metadata. `null` cuando no se conoce. */
  peso: number | null;
  ocupado?: boolean;
  error?: string | null;
  onArchivo: (archivo: File) => void;
  /** Sin handler no se ofrece quitar — p. ej. mientras no hay imagen. */
  onQuitar?: () => void;
}) {
  const [arrastrando, setArrastrando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Un contador y no un booleano: `dragleave` dispara también al pasar sobre
  // los hijos del recuadro (el ícono, el texto), así que con un booleano el
  // resaltado parpadea mientras el cursor recorre el interior. El contador
  // solo llega a cero cuando el puntero sale de verdad.
  const profundidad = useRef(0);

  function recibir(archivo: File | undefined) {
    if (archivo) onArchivo(archivo);
  }

  // El efecto se suscribe una sola vez; lo que cambia en cada render se lee
  // de una ref para no desuscribir y volver a suscribir en cada tecla.
  const alPegar = useRef<(e: ClipboardEvent) => void>(() => {});
  alPegar.current = (e) => {
    if (ocupado || e.defaultPrevented) return;
    const imagen = imagenDelPortapapeles(e);
    if (!imagen) return;
    e.preventDefault();
    onArchivo(imagen);
  };

  // PEGAR ESCUCHA EN EL DOCUMENTO Y NO EN EL RECUADRO. El caso real es una
  // captura recién tomada del visor del SII: exigir antes el foco en el
  // recuadro —que al hacerle clic abre el selector de archivos— convertiría
  // un Ctrl+V en tres pasos. Es seguro porque hay un solo recuadro montado por
  // pantalla, y el filtro de `imagenDelPortapapeles` deja pasar los pegados
  // que son de otro campo.
  useEffect(() => {
    const escuchar = (e: ClipboardEvent) => alPegar.current(e);
    document.addEventListener("paste", escuchar);
    return () => document.removeEventListener("paste", escuchar);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-label text-ink uppercase">Imagen satelital de la propiedad</p>
          <p className="text-ink-hint text-xs">
            Referencial, con el polígono según cartografía SII. Se imprime en la cotización.
          </p>
        </div>

        {src && onQuitar ? (
          <Button type="button" variant="ghost" size="sm" disabled={ocupado} onClick={onQuitar}>
            <Trash2Icon />
            Quitar
          </Button>
        ) : null}
      </div>

      {/* El recuadro es un botón: se activa con click, con Enter y con Espacio.
          `type="button"` NO es decorativo — este componente se usa dentro del
          formulario del informe, y un botón sin type dentro de un form lo
          ENVÍA: soltar una imagen crearía el informe a medio llenar. */}
      <button
        type="button"
        disabled={ocupado}
        aria-label={
          src
            ? "Reemplazar la imagen satelital de la propiedad"
            : "Agregar la imagen satelital de la propiedad"
        }
        onClick={() => inputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          profundidad.current += 1;
          setArrastrando(true);
        }}
        onDragOver={(e) => {
          // Sin este `preventDefault` el navegador NO considera al elemento un
          // destino válido: `drop` nunca dispara y, peor, la ventana navega al
          // archivo soltado y la persona pierde el formulario que estaba
          // llenando.
          e.preventDefault();
        }}
        onDragLeave={() => {
          profundidad.current -= 1;
          if (profundidad.current <= 0) {
            profundidad.current = 0;
            setArrastrando(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          profundidad.current = 0;
          setArrastrando(false);
          recibir(e.dataTransfer.files[0]);
        }}
        className={[
          "relative flex w-full flex-col items-center justify-center gap-2",
          "rounded-xl border-[1.5px] border-dashed p-6 transition-colors",
          "focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-60",
          arrastrando
            ? "border-primary bg-primary/5"
            : "border-input hover:border-primary/50 hover:bg-accent/40",
        ].join(" ")}
      >
        {src ? (
          <>
            {/* `key` con la url: sin él React reutiliza el mismo nodo al
                reemplazar la imagen y, como la URL cambió pero el elemento no,
                algunos navegadores siguen pintando la anterior. */}
            <img
              key={src}
              src={src}
              alt="Imagen satelital de la propiedad"
              className="max-h-72 w-full rounded-md object-contain"
            />
            <span className="text-ink-hint text-xs">
              Arrastra o pega otra imagen, o haz clic para reemplazarla
              {peso === null ? null : (
                <>
                  {" · "}
                  <span className="font-mono">{formatearPeso(peso)}</span>
                </>
              )}
            </span>
          </>
        ) : (
          <>
            <span className="bg-muted text-ink-muted flex size-11 items-center justify-center rounded-full">
              {ocupado ? <Spinner /> : <ImageIcon className="size-5" />}
            </span>
            <span className="text-body text-ink-body font-semibold">
              Arrastra la imagen aquí, pégala o haz clic para buscarla
            </span>
            <span className="text-ink-hint text-xs">JPG, PNG o WEBP · hasta 10 MB</span>
          </>
        )}

        {/* La capa de "subiendo" va ENCIMA en vez de reemplazar el contenido:
            durante un reemplazo, quitar la imagen anterior de la vista dejaría
            el recuadro vacío y daría a entender que ya se borró. */}
        {ocupado && src ? (
          <span className="bg-background/70 absolute inset-0 flex items-center justify-center rounded-xl">
            <Spinner />
          </span>
        ) : null}
      </button>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={EXTENSIONES_ACEPTADAS}
        aria-label="Imagen satelital de la propiedad"
        onChange={(e) => {
          recibir(e.target.files?.[0]);
          // Se limpia para que elegir DOS VECES el mismo archivo vuelva a
          // disparar `change`: sin esto, quien reintenta tras un error tiene
          // que elegir otro archivo primero para que la pantalla reaccione.
          e.target.value = "";
        }}
      />

      {error ? <p className="text-destructive text-sm">{error}</p> : null}
    </div>
  );
}

/**
 * El peso en unidades legibles. Metadata: se muestra en mono y apagado.
 *
 * Exportada porque la lista de antecedentes muestra lo mismo: una segunda copia
 * terminaría redondeando distinto y la misma cifra se leería de dos formas en la
 * misma pantalla.
 */
export function formatearPeso(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
